// Custom props and giant product replicas for brands, experiential agencies
// and PR teams. Prices, past projects (anonymized), the rush rule and the
// "what we don't do" list are the owner's (2026-11-03, see .seo/truth.md
// "Agency and event pages"). Keyword targets, OpenSEO US 2026-11-03: 3d
// printed props 480, prop replicas 480, prop maker 390, custom props 170,
// giant props 140, oversized props 90; giant lipstick, giant perfume bottle
// (as painted or plated replicas). Owner 2026-11-03: no factices, no foam
// (everything is 3D printed), no clear or glass-look finishes ever; finishes
// are paint, metal plating, and frosted/translucent on some printed parts.
// Every photo is real studio work already shown on the 3D printing and
// trophy pages; none is one of the projects listed in `pastProjects`.
const { photos: printingPhotos } = require('./printing-service')
const { photos: nycPhotos } = require('./printing-nyc')
const { photos: trophyPhotos } = require('./custom-trophy')

const propPricing = {
  smallFrom: 1000,
  hero: '$1,500–$3,500',
  heroSize: '12–24 inches',
  showpiece: 10000,
  showpieceSize: '5 feet',
  modelingFrom: 250,
}

const money = (n) => `$${n.toLocaleString('en-US')}`

const customProps = {
  path: '/services/custom-props/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  title: 'Custom 3D Printed Props & Giant Replicas | NYC Prop Maker',
  description: `Custom props and giant product replicas for NYC activations, trade shows and shoots, 3D printed, painted or plated. Hero props ${propPricing.hero}.`,
  answer: `PrintAWorld is a New York prop maker for brands, experiential agencies and PR teams. We 3D print custom props and giant product replicas (oversized bottles, cans, tubes, compacts and packaging), plus character props, photo-op pieces and booth centerpieces, then sand, paint or metal-plate them in our Brooklyn and Queens studios. Small props start around ${money(propPricing.smallFrom)}, ${propPricing.heroSize} hero props usually cost ${propPricing.hero}, and ${propPricing.showpieceSize} showpieces are around ${money(propPricing.showpiece)}, quoted per project. On a rush, we can start the day the invoice is paid.`,
  hero: 'burger',
}

const photos = {
  burger: {
    ...nycPhotos.burger,
    caption: 'Gold-plated burger sculpture',
  },
  chrome: {
    ...printingPhotos.chrome,
    caption: 'Raw print, then chrome plated',
  },
  carFrame: {
    ...printingPhotos.carFrame,
    caption: 'A large-format scale model',
  },
  figure: {
    ...printingPhotos.figure,
    caption: 'A brand mascot, painted',
  },
  gold: {
    url: `https://images.ctfassets.net/dovsnf6qrcem/${trophyPhotos.gold.file}`,
    width: 1500,
    height: 1000,
    alt: trophyPhotos.gold.alt,
    caption: 'Gold-plated cleats',
  },
  hands: {
    ...nycPhotos.hands,
    caption: 'Gold and blue chrome plating',
  },
  clocks: {
    ...printingPhotos.clocks,
    caption: 'Raw print beside a bronze patina finish',
  },
}

// One feature, two wide and four small fill the 4-column gallery grid.
const gallery = [
  { photo: 'chrome', layout: 'feature' },
  { photo: 'carFrame', layout: 'wide' },
  { photo: 'figure' },
  { photo: 'gold' },
  { photo: 'hands', layout: 'wide' },
  { photo: 'clocks' },
  { photo: 'burger' },
]

const facts = [
  {
    title: 'Price',
    body: `Small props from about ${money(propPricing.smallFrom)}. ${propPricing.heroSize} hero props usually ${propPricing.hero}. ${propPricing.showpieceSize} showpieces around ${money(propPricing.showpiece)}. Every prop is quoted per project.`,
  },
  {
    title: 'Size',
    body: 'From desk size to 6 feet and taller. Big pieces are printed in sections, joined, filled and finished so the seams disappear.',
  },
  {
    title: 'Start from',
    body: `Your product or packaging file (STEP, STL or the dieline), or photos of the product. We model it from a 3D scan or from photos with AI-assisted modeling. 3D modeling starts at ${money(propPricing.modelingFrom)}.`,
  },
  {
    title: 'Finish',
    body: 'Sanded, primed and painted to your brand colors, or metal plated in gold, silver or chrome over the printed form. Some printed parts can be frosted or translucent; replicas are opaque.',
  },
  {
    title: 'Rush',
    body: 'We can start today if the invoice is paid today. A 24-inch painted bust for a private event took 2 days.',
  },
  {
    title: 'Delivery',
    body: 'Delivered in New York City or picked up at our studio by appointment. We also ship to all 50 states.',
  },
]

const propTypes = [
  {
    title: 'Giant product replicas',
    body: 'Hero replicas of your bottle, can, tube, jar or box, scaled to 2, 3 or 5 feet for activations, pop-ups, trade show booths, store windows and shoots.',
  },
  {
    title: 'Character and mascot props',
    body: 'Brand characters and mascots sculpted in 3D and painted in full color, at desk size or as a centerpiece guests stand next to.',
  },
  {
    title: 'Photo-op and UGC moments',
    body: 'Oversized pieces built for the photo: a giant product to pose with, props to hold, or a set piece that makes the post.',
  },
  {
    title: 'Prop trophies for media moments',
    body: 'Oversized or product-shaped trophies for an on-stage handoff, a creator award or a press photo.',
  },
  {
    title: 'Functional replicas',
    body: 'Replicas that guests use, like a skincare tub made into a drinking vessel, with a food-safe insert where it touches the drink.',
  },
  {
    title: 'Display shelves, risers and centerpieces',
    body: 'Shelves, plinths and risers painted to your brand colors, and a hero centerpiece for the booth or counter.',
  },
]

// Real projects, anonymized (owner, 2026-11-03). No client names.
const pastProjects = [
  {
    title: 'Three 5-foot personal-care replicas',
    body: 'Three 5-foot replicas of a global personal-care brand’s products for an activation, about $10,000 each.',
  },
  {
    title: 'Giant donuts',
    body: 'Realistic donut props at 12 inches and life size for a financial brand’s event.',
  },
  {
    title: 'Skincare tubs as drinking vessels',
    body: 'Oversized replicas of a skincare tub, used as drinking vessels at a skincare launch.',
  },
  {
    title: 'Full-color character props',
    body: 'Painted character props for an experiential agency’s activation.',
  },
  {
    title: 'Gold-plated giant lipsticks',
    body: 'Oversized lipstick props, metal plated in gold.',
  },
  {
    title: 'A 24-inch bust in 2 days',
    body: 'A painted 24-inch bust for a private event, modeled, printed and finished in 2 days.',
  },
  {
    title: 'A 57-inch roller-coaster model',
    body: 'A 57-inch display model of a roller coaster for an amusement park.',
  },
  {
    title: 'Pantone-matched display shelves',
    body: 'Display shelves painted to the brand’s Pantone colors for an ad agency’s event.',
  },
]

// Oversized beauty product replicas: opaque, painted or metal plated.
const beautyReplicas = {
  intro:
    'Giant lipsticks, compacts, skincare jars, tubes and perfume bottles for pop-ups, store windows, launches and shoots. Every piece is 3D printed and opaque: painted to the shade or metal plated.',
  items: [
    {
      title: 'Giant lipsticks',
      body: 'Lipstick bullets and cases scaled up, painted to the shade or plated in gold like the giant lipstick props we made.',
    },
    {
      title: 'Compacts, palettes and jars',
      body: 'Compacts, palettes and skincare jars as oversized centerpieces, painted in brand colors with the logo.',
    },
    {
      title: 'Tubes, pumps and bottles',
      body: 'Skincare tubes, pump bottles and perfume bottles as painted or plated giant replicas for a window or a launch.',
    },
  ],
  steps: [
    {
      title: '1. Scan or file',
      body: 'We 3D scan the actual product, or work from your packaging file.',
    },
    {
      title: '2. Scale',
      body: 'We scale it in CAD to the size you need, in sections when it’s large.',
    },
    {
      title: '3. Print',
      body: 'We 3D print it, then sand it smooth and prime it.',
    },
    {
      title: '4. Finish',
      body: 'We paint it to the shade or metal-plate it, then add the logo.',
    },
  ],
}

const mockups = {
  title: 'Packaging prototypes and pitch mockups',
  body: 'Before tooling, we 3D print compacts, caps, bottles and closures so your team or your client can hold the design. Painted or plated, they work for pitch meetings, internal reviews and early shoots.',
}

const steps = [
  'Send the product file or photos, the size, where it will be used and the date you need it.',
  'We build the 3D model from a scan, your file, or photos with AI-assisted modeling.',
  'You approve the model and the finish before anything prints.',
  'We print it, in sections for big pieces, then sand, prime, paint or plate it.',
  'We deliver in NYC, hold it for pickup, or pack and ship it.',
]

const limits = [
  {
    title: 'Everything is 3D printed',
    body: 'Every prop is 3D printed, in sections for large pieces, then sanded and painted or plated. We don’t cast silicone or make soft, squeezable props.',
  },
  {
    title: 'Opaque finishes',
    body: 'Finishes are paint and metal plating; some printed parts can be frosted or translucent. Replicas are opaque, not see-through.',
  },
  {
    title: 'No solid metal casting',
    body: 'We don’t cast chrome or bronze. A metal look is metal plating over the printed resin: real metal on the surface, not solid metal.',
  },
  {
    title: 'Display, not working products',
    body: 'Replicas look right; they don’t work like the real product. Functional pieces such as drinking vessels use a food-safe insert.',
  },
]

const faqs = [
  {
    question: 'How much do custom props cost?',
    answer: `Small props start around ${money(propPricing.smallFrom)}. Hero props ${propPricing.heroSize} tall usually cost ${propPricing.hero}, and ${propPricing.showpieceSize} showpieces are around ${money(propPricing.showpiece)}. Size, the finish (paint or metal plating), how many you need and the deadline set the price. Every prop is quoted per project in writing.`,
  },
  {
    question: 'How big can you make a giant product replica?',
    answer:
      'Six feet and taller. Large props are printed in sections, joined and finished as one piece. We’ve made 5-foot product replicas for a brand activation and a 57-inch roller-coaster display model.',
  },
  {
    question: 'How fast can you make a prop for an event?',
    answer:
      'On a rush, we can start the day the invoice is paid. We made a painted 24-inch bust for a private event in 2 days. Most large props take longer, so send your event date with the request and we’ll confirm what’s possible.',
  },
  {
    question: 'What do you need to start?',
    answer: `The product or packaging file if you have it (STEP, STL or the dieline), or photos of the product from every side with one real measurement. Tell us the size, the finish, where the prop will be used and the date. 3D modeling starts at ${money(propPricing.modelingFrom)}.`,
  },
  {
    question: 'Can you make a giant lipstick or perfume bottle replica?',
    answer:
      'Yes, as an opaque painted or metal-plated replica. We 3D scan the product or use the packaging file, scale it up, print it, sand it and paint or plate it. We made gold-plated giant lipstick props.',
  },
  {
    question: 'Can a giant replica be used to serve drinks?',
    answer:
      'Yes, with a food-safe insert where the drink goes. We made oversized skincare-tub replicas that were used as drinking vessels at a skincare launch.',
  },
  {
    question: 'Are gold and chrome props real metal?',
    answer:
      'The surface can be. We metal-plate the printed resin, so it’s a real metal layer over the prop, not solid metal. We don’t cast chrome or bronze.',
  },
  {
    question: 'Can you match our brand colors?',
    answer:
      'We paint to your brand colors and check the color with you before delivery. Send Pantone references; we matched Pantone colors on display shelves for an ad agency’s event.',
  },
  {
    question: 'How are your props made?',
    answer:
      'Everything we make is 3D printed, in sections for large pieces, then sanded, painted or metal plated. We don’t cast silicone.',
  },
  {
    question: 'Can you make packaging prototypes before tooling?',
    answer:
      'Yes. We 3D print compacts, caps, bottles and closures for pitch meetings and reviews, painted or plated to look finished, before the packaging goes to tooling.',
  },
  {
    question: 'Do you deliver props in New York?',
    answer:
      'Yes, we deliver in New York City or you can pick up at our studio by appointment. We also ship to all 50 states. If a piece needs assembly on site, tell us and we’ll include it in the quote.',
  },
]

const sendList = [
  'The product file, or photos from every side',
  'The size, and how many you need',
  'Where it’s going: booth, window, stage or shoot',
  'Your event or shoot date and the delivery address',
]

module.exports = {
  customProps,
  propPricing,
  photos,
  gallery,
  facts,
  propTypes,
  pastProjects,
  beautyReplicas,
  mockups,
  steps,
  limits,
  faqs,
  sendList,
}
